import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  Laptop as LaptopIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { paths } from '../../utils/paths';

const W2ReminderModal: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const STORAGE_KEY = 'w2_reminder_seen_session';
  // Check if it is December (month 11)
  const isDecember = new Date().getMonth() === 11;

  useEffect(() => {
    if (isDecember) {
      const hasSeen = sessionStorage.getItem(STORAGE_KEY);
      if (!hasSeen) {
        setOpen(true);
      }
    }
  }, [isDecember]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isDecember) {
    return null;
  }

  const currentYear = new Date().getFullYear();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 0.5,
              borderRadius: 1,
              display: 'flex',
            }}
          >
            <LaptopIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            Electronic W-2s for {currentYear}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={4} alignItems="center">
            {/* Hero Section */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                }}
              >
                <DescriptionIcon
                  sx={{
                    fontSize: 80,
                    color: alpha(theme.palette.primary.main, 0.2),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: '45%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontWeight: 900,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                  }}
                >
                  W-2
                </Typography>
              </Box>
              <Typography variant="h3" color="text.primary" fontWeight="bold">
                W-2 season is coming!
              </Typography>
            </Box>

            <Typography
              variant="subtitle1"
              color="text.primary"
              sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
            >
              Be sure to review your personal information such as home address
              and name by <strong>December 31</strong> to ensure accuracy of
              your W-2.
            </Typography>

            <Box
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderRadius: 2,
                p: 3,
                maxWidth: 750,
                mx: 'auto',
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              }}
            >
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                Click{' '}
                <Link
                  component={RouterLink}
                  to={paths.w2}
                  onClick={handleClose}
                  sx={{ fontWeight: 'bold' }}
                >
                  this link
                </Link>{' '}
                and make an election if you would like to receive electronic W2
                <br />
                through Employee Portal only.
                <br />
                <br />
                If you make this election, you will not receive a paper W2 in
                the mail. If you don't make this election: (i) you will receive
                a paper copy of your W2 mailed to the home address you have on
                file with us, and (ii) you will have access to an electronic
                version of your W2 via Employee Portal unless your Employer
                opted out of electronic W2s.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ width: '100%', pt: 1 }}
            >
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to={paths.profile}
                onClick={handleClose}
                sx={{ minWidth: 200 }}
              >
                Update personal info
              </Button>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to={paths.w2}
                onClick={handleClose}
                sx={{ minWidth: 200 }}
              >
                Go to W-2 Page
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end',
            bgcolor: 'background.paper',
          }}
        >
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default W2ReminderModal;
